import { useEffect, useMemo, useState } from "react";
import FoodPlanViewer from "../components/FoodPlanViewer";
import FoodLogEntry from "../components/FoodLogEntry";
import DailyTotals from "../components/DailyTotals";
import FoodHistory from "../components/FoodHistory";
import { api } from "../utils/api";
import type { FoodLogPayload } from "../types/api";
import { colors } from "@forge/ui";

type MealItem = {
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
};

type FoodPlan = {
  meals: {
    Breakfast?: MealItem[];
    Lunch?: MealItem[];
    Dinner?: MealItem[];
    Snacks?: MealItem[];
  };
  daily_target_calories?: number;
};

type HistoryDay = {
  date: string;
  totals: { calories: number; protein: number; carbs: number; fat: number };
  items?: Array<{
    name: string;
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    meal: string;
  }>;
};

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const FoodPage: React.FC = () => {
  const [plan, setPlan] = useState<FoodPlan | null>(null);
  const [history, setHistory] = useState<HistoryDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(
    formatDate(new Date()),
  );
  const [loadingPlan, setLoadingPlan] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [planData, logsData] = await Promise.all([
          api.get<FoodPlan>("/api/user/plans/food"),
          api.get<HistoryDay[]>("/api/user/food-logs"),
        ]);
        if (cancelled) return;
        setPlan(Array.isArray(planData) ? planData[0] ?? null : planData);
        setHistory(logsData);
      } catch (e) {
        console.error("Failed to load food data", e);
      } finally {
        if (!cancelled) setLoadingPlan(false);
      }
    })();
    return () => { cancelled = true; };
    // intentionally mount-only: re-running on every render would loop forever
    // and the data doesn't change unless the user adds a log (handled below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLogs = async () => {
    try {
      const data = await api.get<HistoryDay[]>("/api/user/food-logs");
      setHistory(data);
    } catch (e) {
      console.error("Failed to load food logs", e);
    }
  };

  const addLog = async (payload: FoodLogPayload) => {
    try {
      await api.post<unknown>("/api/user/food-logs", payload);
      await fetchLogs();
    } catch (e) {
      console.error("Error posting food log", e);
    }
  };

  const dayTotals = history.find((d) => d.date === selectedDate)?.totals || {
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
  };

  const targets = useMemo(() => {
    if (!plan) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    let protein = 0, carbs = 0, fat = 0, mealCalories = 0;
    const meals = plan.meals;
    for (const mealKey of ["Breakfast", "Lunch", "Dinner", "Snacks"] as const) {
      for (const food of meals[mealKey] ?? []) {
        protein += food.protein ?? 0;
        carbs += food.carbs ?? 0;
        fat += food.fat ?? 0;
        mealCalories += food.calories ?? 0;
      }
    }
    return {
      calories: plan.daily_target_calories ?? mealCalories,
      protein,
      carbs,
      fat,
    };
  }, [plan]);

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.brand}>Forge Food Planner</h1>
      </header>

      <main style={styles.main}>
        <section aria-label="Food plan" style={styles.section}>
          <FoodPlanViewer plan={plan || null} />
          {loadingPlan && <div style={styles.loading}>Loading plan…</div>}
        </section>

        <section aria-label="Daily log" style={styles.section}>
          <h2 style={styles.sectionTitle}>Daily Food Log</h2>
          <div style={styles.dateRow}>
            <label htmlFor="food-date">Date</label>
            <input
              id="food-date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={styles.dateInput}
            />
          </div>
          <FoodLogEntry date={selectedDate} onSubmit={addLog} />
        </section>

        <section aria-label="Daily totals" style={styles.section}>
          <DailyTotals totals={dayTotals} targets={targets} />
        </section>

        <section aria-label="History" style={styles.section}>
          <FoodHistory history={history} />
        </section>
      </main>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  page: {
    padding: 12,
    background: colors.bg,
    minHeight: "100vh",
    color: colors.text,
  },
  header: { marginBottom: 8 },
  brand: { margin: 0, fontSize: 20 },
  main: { display: "flex", flexDirection: "column", gap: 12 },
  section: { marginBottom: 12, padding: 8 },
  sectionTitle: { fontSize: 18, margin: "6px 0" },
  loading: { color: colors.textDim },
  dateRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 6,
    marginBottom: 6,
  },
  dateInput: {
    padding: 10,
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text,
  },
};

export default FoodPage;
